import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';

import { ActionMenu } from '@/components/common/action-menu';
import type { Role } from '@/types';

import { canReviewRequest } from '../../permissions';
import type { WarrantyClaimRow } from '../types';

/** Row kebab for the warranty queue (Figma column 637:345924, menu 30:417165). Actions exist
 *  **only while the claim is Pending** (AC1) — Approved and Rejected are final, so nothing renders
 *  at all and the hover-reveal CSS has nothing to show. Approve sits above Reject and is *not* a
 *  danger item (Figma paints it in the default label colour, Reject in Error/900).
 *  Vertical dots per Figma = the shared trigger's default (no `horizontal`). */
export function WarrantyActionsMenu({
  row,
  viewerRole,
  onApprove,
  onReject,
}: {
  row: WarrantyClaimRow;
  /** The signed-in admin's role, from `useAuth().user` on the queue page. Required, not
   *  optional: a default would re-open this gate the next time a queue is built. */
  viewerRole: Role | null;
  onApprove: (row: WarrantyClaimRow) => void;
  onReject: (row: WarrantyClaimRow) => void;
}) {
  if (row.claimStatus !== 'pending') return null;
  // Row status decides whether the request is actionable at all; the viewer's role decides
  // whether *this* admin may act on it. Both must hold. No ellipsis rather than disabled items:
  // every other branch in this file returns `null`, and an Operational Associate's view of the
  // queue is read-only, so an always-dead trigger would be chrome with nothing behind it.
  if (!canReviewRequest(viewerRole)) return null;

  return (
    <ActionMenu
      triggerLabel={`Actions for ${row.orderNumber}`}
      groups={[
        {
          items: [
            {
              key: 'approve',
              label: 'Approve',
              icon: <CheckCircleIcon size={16} />,
              onClick: () => onApprove(row),
            },
            {
              key: 'reject',
              label: 'Reject',
              icon: <XCircleIcon size={16} />,
              danger: true,
              onClick: () => onReject(row),
            },
          ],
        },
      ]}
    />
  );
}
